import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { saveMessageContactForm } from "../services/contactformServices";
import useScrollToTop from "../hooks/useScrollToTop";
import { getSettings } from "../services/settingsServices";
import { LanguageContext } from "../contexts/LanguageContext";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaCheckCircle } from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch {
    return false;
  }
};

const absUrl = (u) => (u ? (isExternalURL(u) ? u : `${BASE_URL}${u}`) : "");

const ContactPage = () => {
  useScrollToTop();

  const { language } = useContext(LanguageContext);

  const [clubInfo, setClubInfo] = useState(null);
  const [notificationSent, setNotificationSent] = useState(false);
  const [sending, setSending] = useState(false);

  const [formData, setFormData] = useState({ name: "", email: "", message: "" });

  useEffect(() => {
    const fetchClubInfo = async () => {
      try {
        const response = await getSettings();
        if (response?.length > 0) setClubInfo(response[0]);
      } catch (error) {
        console.error("Erreur lors de la récupération des informations :", error);
      }
    };

    fetchClubInfo();
  }, []);

  const labels = useMemo(() => {
    const FR = {
      pageTitle: "Contact",
      pageSubtitle: "Écrivez-moi, je vous répondrai au plus vite.",
      cardTitle: "Coordonnées",
      formTitle: "Formulaire de contact",
      email: "Votre email",
      name: "Nom Prénom",
      message: "Votre message",
      send: "Envoyer",
      sending: "Envoi…",
      success: "Votre message a bien été envoyé !",
      loading: "Chargement des informations…",
      addressMissing: "Adresse non renseignée",
      phone: "Téléphone",
      mail: "Email",
    };

    const EN = {
      pageTitle: "Contact",
      pageSubtitle: "Write to me — I’ll get back to you as soon as possible.",
      cardTitle: "Contact details",
      formTitle: "Contact Form",
      email: "Your email",
      name: "Name",
      message: "Your message",
      send: "Send",
      sending: "Sending…",
      success: "Your message has been sent successfully!",
      loading: "Loading information…",
      addressMissing: "Address not provided",
      phone: "Phone",
      mail: "Email",
    };

    const JP = {
      pageTitle: "お問い合わせ",
      pageSubtitle: "お気軽にご連絡ください。できるだけ早く返信します。",
      cardTitle: "連絡先",
      formTitle: "お問い合わせフォーム",
      email: "メールアドレス",
      name: "名前",
      message: "メッセージ",
      send: "送信",
      sending: "送信中…",
      success: "メッセージが送信されました！",
      loading: "情報を読み込んでいます…",
      addressMissing: "住所が指定されていません",
      phone: "電話番号",
      mail: "メール",
    };

    if (language === "EN") return EN;
    if (language === "JP") return JP;
    return FR;
  }, [language]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNotificationSent(false);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (sending) return;

      const { name, email, message } = formData;

      try {
        setSending(true);
        await saveMessageContactForm({ name, email, message });
        setNotificationSent(true);
        setFormData({ name: "", email: "", message: "" });
      } catch (error) {
        console.error("Erreur lors de l'envoi du message :", error);
      } finally {
        setSending(false);
      }
    },
    [formData, sending]
  );

  if (!clubInfo) {
    return (
      <p className="text-center text-gray-700 dark:text-gray-200 py-12">
        {labels.loading}
      </p>
    );
  }

  // UI helpers
  const sectionWrap = "relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8";
  const softCard =
    "rounded-3xl border border-black/10 bg-[#E8BB89]/80 shadow-sm backdrop-blur-sm " +
    "dark:bg-white/5 dark:border-white/10 dark:shadow-none";
  const softText = "text-gray-900 dark:text-white";
  const mutedText = "text-gray-800 dark:text-gray-300";

  const inputBase =
    "w-full rounded-2xl px-4 py-3 text-sm outline-none transition " +
    "border border-black/10 dark:border-white/10 " +
    "bg-white/70 dark:bg-black/20 " +
    "text-gray-900 dark:text-white " +
    "placeholder:text-gray-500 dark:placeholder:text-gray-400 " +
    "focus:ring-2 focus:ring-primary focus:border-transparent";

  const buttonPrimary =
    "inline-flex items-center justify-center rounded-2xl bg-primary px-6 py-3 text-white shadow " +
    "hover:opacity-95 transition active:scale-[0.99] " +
    "disabled:opacity-60 disabled:cursor-not-allowed";

  const logoUrl =
    absUrl(clubInfo.logo_club) || "/uploads/settings/default_logo.webp";

  return (
    <div className="relative min-h-screen pb-12">
      {/* Décor */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute top-[60px] left-0 w-full h-[34vh] bg-cover bg-no-repeat bg-bottom opacity-60 dark:opacity-25"
          style={{ backgroundImage: "url('/images/fond_sakura.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/35 to-transparent dark:from-black/60" />
      </div>

      {/* HERO */}
      <section className={`${sectionWrap} pt-24 sm:pt-28`}>
        <div className="text-center">
          <div
            className="
              inline-flex items-center gap-2 rounded-full px-4 py-2
              bg-black/5 dark:bg-white/10
              border border-black/10 dark:border-white/10
              backdrop-blur-sm
            "
          >
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs sm:text-sm font-satoshi font-semibold tracking-wide text-gray-700 dark:text-gray-200">
              {labels.pageTitle}
            </span>
          </div>

          <h1 className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-satoshi font-extrabold tracking-tight ${softText}`}>
            {labels.pageTitle}
          </h1>

          <div className="mt-4 flex justify-center">
            <div className="h-[3px] w-24 sm:w-32 rounded-full bg-primary/90" />
          </div>

          <p className={`mt-4 max-w-2xl mx-auto text-sm sm:text-base ${mutedText}`}>
            {labels.pageSubtitle}
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className={`${sectionWrap} mt-10`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT: infos */}
          <aside className={`lg:col-span-5 ${softCard} p-6 sm:p-7`}>
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-48 h-48 sm:w-48 sm:h-48 rounded-3xl bg-white/60 dark:bg-white/10 border border-black/10 dark:border-white/10 flex items-center justify-center overflow-hidden">
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                />
              </div>

              <div className="min-w-0">
                <h2 className={`text-xl sm:text-4xl font-satoshi font-extrabold tracking-tight ${softText}`}>
                  {clubInfo.nom_club}
                </h2>
                <p className={`mt-1 text-sm ${mutedText}`}>
                  {clubInfo.responsable_club}
                </p>
              </div>
            </div>

            <div className="mt-6 h-px bg-black/10 dark:bg-white/10" />

            <h3 className={`mt-6 text-sm font-semibold uppercase tracking-wider ${mutedText}`}>
              {labels.cardTitle}
            </h3>

            <div className="mt-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-black/5 dark:bg-white/10 flex items-center justify-center">
                  <FaMapMarkerAlt className="text-primary dark:text-white" />
                </div>
                <div className="text-sm">
                  <div className={`font-semibold ${softText}`}>{clubInfo.adresse_club || labels.addressMissing}</div>
                </div>
              </div>

              <a
                href={clubInfo.tel_club ? `tel:${clubInfo.tel_club}` : undefined}
                className="flex items-start gap-3 group"
              >
                <div className="w-10 h-10 rounded-2xl bg-black/5 dark:bg-white/10 flex items-center justify-center">
                  <FaPhoneAlt className="text-primary dark:text-white" />
                </div>
                <div className="text-sm">
                  <div className={`font-semibold ${softText}`}>
                    {labels.phone} :{" "}
                    <span className="group-hover:underline">{clubInfo.tel_club || "—"}</span>
                  </div>
                </div>
              </a>

              <a
                href={clubInfo.mail_club ? `mailto:${clubInfo.mail_club}` : undefined}
                className="flex items-start gap-3 group"
              >
                <div className="w-10 h-10 rounded-2xl bg-black/5 dark:bg-white/10 flex items-center justify-center">
                  <FaEnvelope className="text-primary dark:text-white" />
                </div>
                <div className="text-sm">
                  <div className={`font-semibold ${softText}`}>
                    {labels.mail} :{" "}
                    <span className="text-primary group-hover:underline dark:text-white">
                      {clubInfo.mail_club || "—"}
                    </span>
                  </div>
                </div>
              </a>
            </div>

            <div className="mt-6">
              <a href="/prise_rdv" className="inline-flex text-primary font-semibold hover:underline dark:text-white">
                {language === "FR" ? "Prendre rendez-vous →" : language === "EN" ? "Book an appointment →" : "予約する →"}
              </a>
            </div>
          </aside>

          {/* RIGHT: form */}
          <div className={`lg:col-span-7 ${softCard} p-6 sm:p-7`}>
            <div className="flex items-center justify-between gap-4">
              <h2 className={`text-xl sm:text-2xl font-satoshi font-extrabold tracking-tight ${softText}`}>
                {labels.formTitle}
              </h2>

              {notificationSent && (
                <div className="hidden sm:flex items-center gap-2 text-sm text-primary font-semibold">
                  <FaCheckCircle />
                  <span>{labels.success}</span>
                </div>
              )}
            </div>

            <p className={`mt-2 text-sm ${mutedText}`}>
              {language === "FR"
                ? "Remplissez le formulaire ci-dessous. Je vous répondrai par email."
                : language === "EN"
                ? "Fill out the form below. I’ll reply by email."
                : "下記フォームにご記入ください。メールで返信します。"}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className={`block mb-2 text-sm font-semibold ${softText}`}>
                    {labels.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={inputBase}
                    placeholder="email@example.com"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label htmlFor="name" className={`block mb-2 text-sm font-semibold ${softText}`}>
                    {labels.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={inputBase}
                    placeholder={language === "FR" ? "Votre Nom Prénom" : language === "EN" ? "Your name" : "お名前"}
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    autoComplete="name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className={`block mb-2 text-sm font-semibold ${softText}`}>
                  {labels.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={7}
                  className={inputBase}
                  placeholder={language === "FR" ? "Votre message ici..." : language === "EN" ? "Your message here..." : "メッセージを入力してください..."}
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </div>

              {/* action row */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <button type="submit" className={buttonPrimary} disabled={sending}>
                  {sending ? labels.sending : labels.send}
                </button>

                {notificationSent && (
                  <div className="sm:hidden w-full flex items-center gap-2 text-sm text-primary font-semibold">
                    <FaCheckCircle />
                    <span>{labels.success}</span>
                  </div>
                )}
              </div>

              {/* hint */}
              <p className="text-xs text-gray-700/80 dark:text-gray-300/80">
                {language === "FR"
                  ? "En envoyant ce formulaire, vous acceptez d’être recontacté par email."
                  : language === "EN"
                  ? "By submitting this form, you agree to be contacted by email."
                  : "送信すると、メールで連絡を受け取ることに同意したものとみなされます。"}
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* small success toast (desktop) */}
      {notificationSent && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[999] hidden sm:block">
          <div className="flex items-center gap-2 rounded-2xl bg-white/90 dark:bg-neutral-950/90 px-4 py-3 shadow-lg border border-black/10 dark:border-white/10 backdrop-blur">
            <FaCheckCircle className="text-primary" />
            <span className="text-sm text-gray-900 dark:text-white font-semibold">
              {labels.success}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactPage;
